import { useEffect } from 'react';
import { useRevalidator } from 'react-router-dom';
import {
  RefreshStateSchema,
  type RefreshState,
} from '@tinypro-audit/contracts';
import { ApiClientError, fetchJson } from './client';

/** Poll small metadata; fetch reports again only after their underlying data changes. */
export function usePageRefresh(
  refresh: RefreshState | undefined,
  projectId?: string,
  includeSource = true,
) {
  const revalidator = useRevalidator();
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', projectId);
  if (!includeSource) params.set('scope', 'portal');
  const url = `/api/projects/activity?${params}`;
  useEffect(() => {
    let stopped = false;
    let busy = false;
    let failures = 0;
    const controller = new AbortController();
    let delay = refresh?.pending ? 15_000 : 60_000;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        if (!document.hidden && revalidator.state === 'idle') {
          const next = await fetchJson(
            url,
            { signal: controller.signal },
            RefreshStateSchema,
          );
          if (!stopped && next.revision !== refresh?.revision)
            await revalidator.revalidate();
          failures = 0;
          delay = next.pending ? 15_000 : 60_000;
        }
      } catch (error) {
        if (!stopped && error instanceof ApiClientError && error.status === 401)
          await revalidator.revalidate();
        delay = Math.min(120_000, 15_000 * 2 ** Math.min(++failures, 3));
      } finally {
        busy = false;
        if (!stopped) timer = setTimeout(() => void poll(), delay);
      }
    };
    const onVisible = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        void poll();
      }
    };
    timer = setTimeout(() => void poll(), delay);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [url, refresh?.revision, refresh?.pending, revalidator]);
}
